import {DropdownDivider, useTranslationContext} from 'platform/components';
import {Icon, Show, type ThemeIconKey} from 'platform/foundation';

import {useColumnMenuContext} from '../../../../../../../../context/ColumnMenuContext';
import {useClipboardExportAllowed} from '../../../../../../../../store/selectors/useBehavior';
import {useCanCopyColumn} from '../../../../../../../../store/selectors/useCanCopyColumn';
import {PIN_OPTIONS} from './PIN_OPTIONS';
import {SORT_OPTIONS} from './SORT_OPTIONS';

type ColumnMenuOptionsTabMobileProps = {
  currentPinned: 'LEFT' | 'RIGHT' | null | undefined;
  /** Key of the column this menu belongs to — used to call onAutosizeColumn / onPinColumn */
  columnKey: string;
  /** Whether the column supports sorting — controls the Sort section's visibility */
  columnSortable: boolean;
  /** Called after any action to close the parent MobileSheet */
  onClose: () => void;
};

type OptionButtonProps = {
  label: string;
  onClick: () => void;
  isActive?: boolean;
  isDisabled?: boolean;
  leftIcon?: ThemeIconKey;
  'data-testid': string;
};

/**
 * Full-width plain-button row — same visual treatment as MobileRowActionSheet's
 * items (flex row, icon + label, py-3, active:bg-slate-100). The active/selected
 * state (current sort direction, current pin) mirrors DropdownItem's desktop
 * treatment (blue text + bold) rather than a checkmark, since the theme icon
 * registry used here (platform/foundation) has no check/checkmark glyph.
 */
function OptionButton(props: OptionButtonProps) {
  return (
    <button
      type="button"
      disabled={props.isDisabled}
      onClick={props.onClick}
      className={`flex items-center gap-3 w-full text-left px-3 py-3 text-[15px] rounded-lg ${
        props.isActive ? 'text-[#0065FF] font-semibold' : 'text-[#091E42]'
      } ${props.isDisabled ? 'opacity-40 cursor-not-allowed' : 'active:bg-slate-100'}`}
      data-testid={props['data-testid']}
    >
      <Show when={props.leftIcon}>
        <Icon value={props.leftIcon} size={4} />
      </Show>
      <span className="flex-1">{props.label}</span>
    </button>
  );
}

/**
 * Mobile variant of the Options tab content — renders the SAME actions as
 * ColumnMenuOptionsTab (desktop), wired to the same ColumnMenuContext handlers,
 * but as plain full-width buttons instead of DropdownItem/DropdownSubmenu.
 *
 * Why a separate component instead of reusing ColumnMenuOptionsTab as-is:
 * DropdownItem's onClick only fires when rendered inside a real
 * <Dropdown>/<DropdownContainer> ancestor — its click handling comes from a
 * non-exported `MenuContext` supplied by DropdownContainer (a real
 * floating-ui interaction merge); the default context (no ancestor) discards
 * the onClick entirely. Since the mobile container is a MobileSheet (no
 * Dropdown in the tree at all — by design, so its own click-outside/dismiss
 * logic never runs there), DropdownItem cannot be reused verbatim on mobile.
 * Verified empirically: a DropdownItem rendered without a Dropdown ancestor
 * never invokes its onClick (0 calls in a scratch jest spec).
 *
 * DropdownDivider has no such coupling (plain styled <hr>, no FloatingList /
 * MenuContext read) so it IS reused as-is here.
 */
export function ColumnMenuOptionsTabMobile(props: ColumnMenuOptionsTabMobileProps) {
  const t = useTranslationContext();
  const columnMenu = useColumnMenuContext();
  const clipboardExportAllowed = useClipboardExportAllowed();
  const canCopyColumn = useCanCopyColumn(props.columnKey);

  const normalizedPinned = props.currentPinned ?? null;
  const currentSort = columnMenu.columnStates.get(props.columnKey)?.sort ?? null;

  const handleSort = (sort: 'ASC' | 'DESC' | null) => {
    columnMenu.onSort(props.columnKey, sort);
    props.onClose();
  };

  const handlePin = (value: 'LEFT' | 'RIGHT' | null) => {
    columnMenu.onPinColumn(props.columnKey, value);
    props.onClose();
  };

  const handleAutosizeThis = () => {
    columnMenu.onAutosizeColumn(props.columnKey);
    props.onClose();
  };

  const handleAutosizeAll = () => {
    columnMenu.onAutosizeAllColumns();
    props.onClose();
  };

  const handleResetColumns = () => {
    columnMenu.onResetColumns();
    props.onClose();
  };

  const handleResetFilters = () => {
    columnMenu.onResetFilters();
    props.onClose();
  };

  const handleCopyColumn = () => {
    columnMenu.onCopyColumn(props.columnKey);
    props.onClose();
  };

  return (
    <div className="p-2">
      <Show when={props.columnSortable}>
        <>
          {SORT_OPTIONS.map((option) => (
            <OptionButton
              key={option.value ?? 'none'}
              label={t(option.labelKey, {defaultValue: option.defaultLabel})}
              isActive={currentSort === option.value && option.value !== null}
              isDisabled={option.value === null && currentSort === null}
              onClick={() => handleSort(option.value)}
              data-testid={`columnMenuSortOption-${option.value ?? 'none'}`}
            />
          ))}
          <DropdownDivider />
        </>
      </Show>

      <Show when={columnMenu.showPinning}>
        <>
          {PIN_OPTIONS.map((option) => (
            <OptionButton
              key={option.value ?? 'none'}
              label={t(option.labelKey, {defaultValue: option.defaultLabel})}
              isActive={normalizedPinned === option.value}
              onClick={() => handlePin(option.value)}
              data-testid={`columnMenuPinOption-${option.value ?? 'none'}`}
            />
          ))}
          <DropdownDivider />
        </>
      </Show>

      <Show when={columnMenu.showAutosize}>
        <>
          <OptionButton
            label={t('page.datagrid.labels.autosizeThisCol', {
              defaultValue: 'Autosize this column',
            })}
            leftIcon="action/aspect_ratio"
            onClick={handleAutosizeThis}
            data-testid="columnMenuAutosizeThis"
          />
          <OptionButton
            label={t('page.datagrid.labels.autosizeAllCols', {
              defaultValue: 'Autosize all columns',
            })}
            leftIcon="action/aspect_ratio"
            onClick={handleAutosizeAll}
            data-testid="columnMenuAutosizeAll"
          />
        </>
      </Show>

      <DropdownDivider data-testid="columnMenuDivider" />

      <Show when={clipboardExportAllowed}>
        <OptionButton
          label={t('page.datagrid.labels.copyColumnCurrentPage', {
            defaultValue: 'Copy column (current page)',
          })}
          leftIcon="content/copy"
          isDisabled={!canCopyColumn}
          onClick={handleCopyColumn}
          data-testid="columnMenuCopyColumn"
        />
      </Show>

      <OptionButton
        label={t('page.datagrid.labels.resetCols', {defaultValue: 'Reset columns'})}
        leftIcon="action/restore"
        onClick={handleResetColumns}
        data-testid="columnMenuResetColumns"
      />
      <OptionButton
        label={t('page.datagrid.labels.resetAllFilters', {defaultValue: 'Reset filters'})}
        leftIcon="content/filter_list"
        onClick={handleResetFilters}
        data-testid="columnMenuResetFilters"
      />
    </div>
  );
}
